import React from "react";
import { NavLink } from "react-router-dom";
import { checkDeveloperMode } from "../../DeveloperWorkbench/DeveloperWorkbench.utils";
import { mercury } from "@bfme-technology/styled";
import type { SidebarProps } from "./Sidebar.types";
import { useSidebar } from "./Sidebar.hook";
import {
  getSidebarNavLinkClass,
  getSidebarNavIconClass,
} from "./Sidebar.utils";
import * as styles from "./Sidebar.styles";

export const Sidebar: React.FC<SidebarProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}) => {
  const { t, isExpenseActive, isExpenseOpen, setIsExpenseOpen } = useSidebar();

  return (
    <aside
      className={`sidebar-layout !z-[100] ${mercury.getSidebarClass({ isCollapsed: isSidebarCollapsed, isOpen: isSidebarOpen })}`}
    >
      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className={styles.toggleCollapseBtnBase}
        title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        <i
          className={`fa-solid ${isSidebarCollapsed ? "fa-chevron-right" : "fa-chevron-left"} text-xs`}
        ></i>
      </button>

      <button
        onClick={() => setIsSidebarOpen(false)}
        className={styles.closeBtnBase}
        title="Close Sidebar"
      >
        <i className="fa-solid fa-xmark text-sm"></i>
      </button>

      <div className={styles.headerContainer}>
        <div className={styles.logoContainer}>
          <div className={styles.logoGlow}></div>
          {isSidebarCollapsed ? (
            <img
              src="/favicon.svg"
              alt="WealthIQ Icon"
              className={styles.logoImage}
            />
          ) : (
            <div className="flex items-center gap-2.5 relative">
              <img
                src="/logo.svg"
                alt="WealthIQ Icon"
                className="w-8 h-8 object-contain"
              />
              <div className={styles.logoTextContainer}>
                <span className={styles.logoTextBase}>
                  Wealth<span className="text-primary-accent">IQ</span>
                </span>
                <span className={styles.logoSubText}>
                  Smart Asset Management
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className={mercury.navClass}>
        <NavLink
          data-testid="sidebar-nav-dashboard"
          to={checkDeveloperMode() ? "/dashboard?developer=1" : "/dashboard"}
          onClick={() => setIsSidebarOpen(false)}
          className={({ isActive }) =>
            `${getSidebarNavLinkClass(isActive)} ${
              isSidebarCollapsed ? "!pr-2" : ""
            }`
          }
          title={isSidebarCollapsed ? t("dashboard") : undefined}
        >
          {({ isActive }) => (
            <>
              <span className={getSidebarNavIconClass(isActive)}>
                <i className="fa-solid fa-chart-pie"></i>
              </span>
              {!isSidebarCollapsed && (
                <span className={mercury.navLinkTextClass}>
                  {t("dashboard")}
                </span>
              )}
            </>
          )}
        </NavLink>

        {/* EXPENSE GROUP */}
        <div className="flex flex-col relative group">
          <button
            onClick={() => {
              if (isSidebarCollapsed) {
                setIsSidebarCollapsed(false);
                setIsExpenseOpen(true);
              } else {
                setIsExpenseOpen(!isExpenseOpen);
              }
            }}
            className={`${getSidebarNavLinkClass(isExpenseActive && isSidebarCollapsed)} ${isSidebarCollapsed ? "!pr-2" : ""} flex justify-between items-center w-full relative z-10`}
            title={isSidebarCollapsed ? t("expense", "Expense") : undefined}
          >
            <div className="flex items-center">
              <span
                className={getSidebarNavIconClass(
                  isExpenseActive && isSidebarCollapsed,
                )}
              >
                <i className="fa-solid fa-wallet"></i>
              </span>
              {!isSidebarCollapsed && (
                <span className={mercury.navLinkTextClass}>
                  {t("expense", "Expense")}
                </span>
              )}
            </div>
            {!isSidebarCollapsed && (
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isExpenseOpen ? "rotate-180" : ""} text-text-muted`}
              ></i>
            )}
          </button>

          {/* Expanded State Sub-menu */}
          <div
            className={styles.getSubMenuContainerClass(isExpenseOpen, isSidebarCollapsed)}
          >
            <NavLink
              data-testid="sidebar-nav-expense-manager"
              to={
                checkDeveloperMode()
                  ? "/expense/manager?developer=1"
                  : "/expense/manager"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-list-check text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("expenseManager", "Expense Manager")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-automated-tracker"
              to={
                checkDeveloperMode()
                  ? "/expense/automated-tracker?developer=1"
                  : "/expense/automated-tracker"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-robot text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("automatedTracker", "Automated Tracker")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-budgets"
              to={
                checkDeveloperMode()
                  ? "/expense/budgets?developer=1"
                  : "/expense/budgets"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-layer-group text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("budgets", "Budgets & Categories")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-credit-cards"
              to={
                checkDeveloperMode()
                  ? "/expense/credit-cards?developer=1"
                  : "/expense/credit-cards"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-brands fa-cc-visa text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("creditCards", "Credit Cards")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-bank-accounts"
              to={
                checkDeveloperMode()
                  ? "/expense/bank-accounts?developer=1"
                  : "/expense/bank-accounts"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-building-columns text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("bankAccounts", "Bank Accounts")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-banks"
              to={
                checkDeveloperMode()
                  ? "/expense/banks?developer=1"
                  : "/expense/banks"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-list text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("bankList", "Bank List")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-loans"
              to={
                checkDeveloperMode()
                  ? "/expense/loans?developer=1"
                  : "/expense/loans"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-hand-holding-dollar text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("loans", "Loans & EMIs")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-vaults"
              to={
                checkDeveloperMode()
                  ? "/expense/vaults?developer=1"
                  : "/expense/vaults"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Lines */}
                  <div className="absolute left-[24px] top-0 h-full w-px bg-border-color z-0"></div>
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-vault text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("vaults", "Vaults")}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            <NavLink
              data-testid="sidebar-nav-entities"
              to={
                checkDeveloperMode()
                  ? "/expense/entities?developer=1"
                  : "/expense/entities"
              }
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `${getSidebarNavLinkClass(isActive)} h-9 relative z-10`
              }
              style={{ paddingLeft: "44px" }}
            >
              {({ isActive }) => (
                <>
                  {/* Tree Line (Last item) */}
                  <div className="absolute left-[24px] top-0 h-1/2 w-5 border-l border-b border-border-color rounded-bl-lg z-0"></div>
                  <span
                    className={`w-5 inline-block text-center mr-2 z-10 relative ${isActive ? "text-white" : "text-text-muted"}`}
                  >
                    <i className="fa-solid fa-address-book text-xs"></i>
                  </span>
                  {!isSidebarCollapsed && (
                    <span className={mercury.navLinkTextClass}>
                      {t("entities", "Entities Directory")}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          </div>

          {/* Collapsed State Flyout Sub-menu */}
          {isSidebarCollapsed && (
            <div className={styles.getFlyoutMenuClass()}>
              <div className="px-3 py-2 text-[11px] font-bold text-text-muted uppercase tracking-wider border-b border-border-color mb-1">
                {t("expense", "Expense")}
              </div>
              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/manager?developer=1"
                    : "/expense/manager"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9 mt-1`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-list-check text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("expenseManager", "Expense Manager")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/automated-tracker?developer=1"
                    : "/expense/automated-tracker"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-robot text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("automatedTracker", "Automated Tracker")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/budgets?developer=1"
                    : "/expense/budgets"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-layer-group text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("budgets", "Budgets & Categories")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/credit-cards?developer=1"
                    : "/expense/credit-cards"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-brands fa-cc-visa text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("creditCards", "Credit Cards")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/bank-accounts?developer=1"
                    : "/expense/bank-accounts"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-building-columns text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("bankAccounts", "Bank Accounts")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/banks?developer=1"
                    : "/expense/banks"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-list text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("bankList", "Bank List")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/loans?developer=1"
                    : "/expense/loans"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-hand-holding-dollar text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("loans", "Loans & EMIs")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/vaults?developer=1"
                    : "/expense/vaults"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-vault text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("vaults", "Vaults")}
                    </span>
                  </>
                )}
              </NavLink>

              <NavLink
                to={
                  checkDeveloperMode()
                    ? "/expense/entities?developer=1"
                    : "/expense/entities"
                }
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `${getSidebarNavLinkClass(isActive)} px-3 h-9`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-5 inline-block text-center mr-2 ${isActive ? "text-white" : "text-text-muted"}`}
                    >
                      <i className="fa-solid fa-address-book text-xs"></i>
                    </span>
                    <span className={mercury.navLinkTextClass}>
                      {t("entities", "Entities Directory")}
                    </span>
                  </>
                )}
              </NavLink>
            </div>
          )}
        </div>
      </nav>

      <div className="mt-auto pt-4 border-t border-border-color">
        {isSidebarCollapsed ? (
          <div className="flex justify-center">
            <span
              className="inline-block w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"
              title={t("coreInitialized")}
            ></span>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-text-muted">Status:</div>
            <div className="text-xs text-green-400 font-bold flex items-center gap-1.5 mt-1">
              <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>{" "}
              {t("coreInitialized")}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
